import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ShieldCheck, Clock, ExternalLink, AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Dashboard page where a logged-in, email-verified user can add their company
 * to the public Trading Directory or see the current status of their listing.
 *
 * Server flow:
 *   - GET  /directory/my-listing  -> current listing (pending / approved) if any
 *   - POST /directory/submit      -> enters the admin moderation queue (pending)
 *
 * Verification ribbon:
 *   - "Trados Verified" auto-flows from the existing tier system: when the
 *     user's bronze/silver/gold tier is approved, sync_directory_listing_for_user()
 *     upgrades the listing. No extra plumbing here.
 */
const toList = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);

const StatusPill = ({ listing }) => {
  if (listing.verification?.verified) {
    const tier = listing.verification.tier || 'verified';
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <ShieldCheck className="w-3.5 h-3.5" /> Trados Verified · {tier}
      </span>
    );
  }
  if (listing.claim_status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        <Clock className="w-3.5 h-3.5" /> Pending admin review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
      Listed · Not yet verified
    </span>
  );
};

const ExistingListingPanel = ({ listing }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-14 h-14 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {listing.logo_url
            ? <img src={listing.logo_url} alt="" className="w-full h-full object-contain" />
            : <Building2 className="w-7 h-7 text-gray-300" />}
        </div>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-gray-900 truncate">{listing.name}</h2>
          <p className="text-sm text-gray-500 truncate">
            {[listing.city, listing.country].filter(Boolean).join(', ') || '—'}
          </p>
        </div>
      </div>
      <StatusPill listing={listing} />
    </div>

    {listing.claim_status === 'pending' && (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4">
        <p className="text-sm text-amber-900">
          Your submission is in the admin review queue. You'll be notified by email once it's approved.
        </p>
      </div>
    )}

    {!listing.verification?.verified && listing.claim_status === 'approved' && (
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-4">
        <p className="text-sm text-blue-900">
          Your listing is live. To earn the <strong>Trados Verified</strong> ribbon, complete your tier
          verification (Bronze → Silver → Gold).
        </p>
        <Link
          to="/admin/verify/bronze"
          className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          Start tier verification
        </Link>
      </div>
    )}

    <div className="flex flex-wrap gap-2 pt-2">
      <Link
        to={`/directory/${listing.slug}`}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
      >
        View public listing <ExternalLink className="w-3.5 h-3.5" />
      </Link>
    </div>
  </div>
);

const SubmitForm = ({ onSubmitted }) => {
  const [form, setForm] = useState({ name: '', website: '', country: '', city: '', emails: '', phones: '', commodities: '', description: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Company name is required.'); return; }
    setBusy(true);
    try {
      const res = await api.post('/directory/submit', {
        name: form.name.trim(),
        website: form.website.trim() || null,
        emails: toList(form.emails),
        country: form.country.trim() || null,
        city: form.city.trim() || null,
        commodities: toList(form.commodities),
        phones: toList(form.phones),
        description: form.description.trim() || null,
      });
      toast.success(res.data.message || 'Submitted — pending admin review.');
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit.');
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-xl font-bold text-gray-900">Add your company to the Trading Directory</h2>
        <p className="text-sm text-gray-500 mt-1">
          Your submission goes to admin review. Once approved, your listing goes live.
          To earn the <strong>Trados Verified</strong> ribbon, complete your tier verification —
          your listing upgrades automatically.
        </p>
      </div>
      <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {field('name', 'Company name *', 'text', 'e.g. AfriGrain Trading')}
        {field('website', 'Website', 'url', 'https://example.com')}
        {field('country', 'Country', 'text', 'Brazil')}
        {field('city', 'City', 'text', 'São Paulo')}
        {field('emails', 'Emails (comma-separated)', 'text', 'sales@example.com')}
        {field('phones', 'Phones (comma-separated)', 'text', '+1 555 0100')}
        <div className="md:col-span-2">{field('commodities', 'Commodities (comma-separated)', 'text', 'Sugar, Soybeans, Coffee')}</div>
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Short description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            rows={4}
            maxLength={600}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="md:col-span-2 flex justify-end pt-2">
          <button
            type="submit"
            disabled={busy}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {busy ? 'Submitting…' : 'Submit for review'}
          </button>
        </div>
      </form>
    </div>
  );
};

const MyCompanyListing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/directory/my-listing');
      setListing(res.data?.listing || null);
    } catch {
      setListing(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const emailUnverified = user && user.email_verified === false;

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Company Listing</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your presence in the public Trados Trading Directory.</p>
          </div>

          {emailUnverified && (
            <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="text-base font-semibold text-gray-900">Verify your email first</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    You need a verified email address before adding a company to the directory.
                  </p>
                  <button
                    onClick={() => navigate('/admin/verify-email-required')}
                    className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                  >
                    <Mail className="w-4 h-4" /> Verify email
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center text-sm text-gray-400">
              Loading…
            </div>
          ) : listing ? (
            <ExistingListingPanel listing={listing} />
          ) : !emailUnverified ? (
            <SubmitForm onSubmitted={load} />
          ) : null}
        </div>
      </div>
    </Sidebar>
  );
};

export default MyCompanyListing;
